import { useCallback, useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Button, F0Select } from '@factorialco/f0-react'
import {
  Alert,
  AlertDescription,
  OneModal,
  SelectItemProps,
} from '@factorialco/f0-react/dist/experimental'
import {
  AccessForSelectFragment,
  AccessSelectorDocument,
  AtsBulkCreateHiringManagersDocument,
  AtsGetAddHiringManagerModalDocument,
  AtsGetHiringTeamDocument,
} from 'generated/resources/graphql'

import { useGraphqlDataSource } from 'modules/core/hooks/useGraphqlDataSource'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface Props {
  onClose: () => void
  jobId: number
}

export const AddHiringManagerModal = ({ jobId, onClose }: Props) => {
  const { data: addHiringManagerModalData } = useQuery(AtsGetAddHiringManagerModalDocument, {
    variables: {
      jobPostingId: jobId,
    },
  })

  const hiringManagersData = addHiringManagerModalData?.ats.hiringManagersConnection.nodes ?? []

  const [selectedAccessIds, setSelectedAccessIds] = useState<string[]>([])

  const [createHiringManager, { loading }] = useMutation(AtsBulkCreateHiringManagersDocument, {
    extractorFn: (data) => data.atsMutations.bulkCreateAtsHiringManager,
  })

  const accessOptions = useGraphqlDataSource(
    {
      paginationType: 'infinite-scroll',
      perPage: 25,
      document: AccessSelectorDocument,
      search: { enabled: true },
      selectable: (item) => String(item.id),
      mapData: (data) => data.apiCore.accessesConnection.edges.map((edge) => edge.node),
      mapPagination: (data) => {
        const edges = data.apiCore.accessesConnection.edges
        return {
          total: edges.length,
          cursor: data.apiCore.accessesConnection.pageInfo.endCursor ?? '0',
          hasNextPage: !!data.apiCore.accessesConnection.pageInfo.hasNextPage,
        }
      },
      mapVariables: ({ search }) => ({
        search,
        terminated: false,
        excludeIds: [],
      }),
    },
    []
  )

  const mapOptions = useCallback(
    (
      item: AccessForSelectFragment
    ): SelectItemProps<string, AccessForSelectFragment> & { type: 'item' } => ({
      type: 'item',
      value: String(item.id),
      label: `${item.firstName} ${item.lastName}`,
      avatar: {
        type: 'person',
        firstName: item.firstName,
        lastName: item.lastName,
        src: buildUrl(item.avatar),
      },
    }),
    []
  )

  const onSubmit = async () => {
    const result = await createHiringManager({
      variables: {
        accessIds: selectedAccessIds.map((id) => Number(id)),
        jobPostingId: jobId,
      },
      refetchQueries: [AtsGetHiringTeamDocument],
    })

    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
      onClose()
    }
  }

  const handleSelectItems = useCallback(
    (items: {
      allSelected: boolean | 'indeterminate'
      selectedIds: string[]
      itemsStatus: { item: { id: string }; checked: boolean }[]
    }) => {
      setSelectedAccessIds(items.selectedIds)
    },
    []
  )

  const duplicateHiringManagers = hiringManagersData.filter((hm) =>
    selectedAccessIds.some((selectedId) => hm.access?.id === Number(selectedId))
  )

  const showWarning = duplicateHiringManagers.length > 0

  const currentHiringManagerNames = duplicateHiringManagers
    .map((hm) => {
      const access = hm.access
      return access ? `${access.firstName} ${access.lastName}` : ''
    })
    .filter(Boolean)
    .join(', ')

  return (
    <OneModal isOpen onClose={onClose}>
      <OneModal.Header title={i18n.t('ats.job_posting_settings.hiring_team.add_modal.title')} />
      <OneModal.Content withPadding>
        <Box gap="s16">
          <Text.Normal color="grey700">
            {i18n.t('ats.job_posting_settings.hiring_team.add_modal.description')}
          </Text.Normal>
          {showWarning && (
            <Alert variant="warning">
              <AlertDescription>
                {i18n.t(
                  'ats.job_posting_settings.hiring_team.add_modal.already_part_of_hiring_team',
                  {
                    hiring_managers: currentHiringManagerNames,
                  }
                )}
              </AlertDescription>
            </Alert>
          )}
          <F0Select
            source={accessOptions}
            mapOptions={mapOptions}
            label={i18n.t('common.select')}
            value={selectedAccessIds}
            onSelectItems={handleSelectItems}
            showSearchBox
            searchEmptyMessage={i18n.t('ats.jobs.new_job_modal.search_no_results_found')}
            size="md"
            multiple
            disableSelectAll
          />
        </Box>
      </OneModal.Content>
      <OneModal.Footer>
        <Box flexDirection="row" justifyContent="flexEnd" gap="s12" width="full">
          <F0Button onClick={onClose} label={i18n.t('common.cancel')} variant="outline" />
          <F0Button
            onClick={onSubmit}
            label={i18n.t('ats.job_posting_settings.hiring_team.add_modal.button')}
            loading={loading}
            disabled={selectedAccessIds.length === 0 || showWarning}
          />
        </Box>
      </OneModal.Footer>
    </OneModal>
  )
}
